import type { Ref } from "vue";

import {
  stickyNoteBulkActionConfirmed,
  stickyNoteBulkActionOpened,
} from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";

type BlockedGroup = readonly [name: string, items: Ref<readonly unknown[]>];

/**
 * Wires `web.sticky_note.bulk_action_opened` / `bulk_action_confirmed` for a bulk
 * action button. Both handlers no-op for single-card flows so they're safe to wire
 * up in components that share their primary handler with the single-card variant.
 *
 * `groups` lists every potential blocked group; only those whose ref is non-empty
 * at open-time are included in the tracked event.
 */
export function useBulkActionTracking(
  bulkAction: "unplan" | "remove" | "delete",
  cards: Ref<readonly unknown[]>,
  groups: readonly BlockedGroup[],
) {
  const isBulk = () => cards.value.length > 1;
  const blockedGroups = () =>
    groups.filter(([_name, items]) => items.value.length > 0).map(([name]) => name);

  return {
    trackOpened: () => {
      if (!isBulk()) return;
      trackEvent(stickyNoteBulkActionOpened(bulkAction, cards.value.length, blockedGroups()));
    },
    trackConfirmed: () => {
      if (!isBulk()) return;
      trackEvent(stickyNoteBulkActionConfirmed(bulkAction, cards.value.length, blockedGroups()));
    },
  };
}
